// Lazy-loading looping video: shows its poster instantly, downloads and plays
// the video only when it approaches the viewport. Keeps every backdrop moving
// without front-loading all videos on page load.
import { useEffect, useRef } from "react";

interface LazyVideoProps {
  src: string;
  poster?: string;
  className?: string;
}

export default function LazyVideo({ src, poster, className }: LazyVideoProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Reduced motion: keep the still poster, never download the video.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !video.src) {
            video.src = src;
            video.play().catch(() => {
              /* autoplay rejection — poster remains, no broken state */
            });
            io.unobserve(video);
          }
        }
      },
      // Start fetching just before the tile scrolls into view.
      { rootMargin: "300px 0px" },
    );

    io.observe(video);
    return () => io.disconnect();
  }, [src]);

  return (
    <video
      ref={videoRef}
      className={className}
      poster={poster}
      muted
      loop
      playsInline
      preload="none"
    />
  );
}
